import React, { useMemo, useState } from "react";
import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Alert,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { SubmissionReviewActivity, ParticipantTeam } from "../../types";
import {
  SET_MANUAL_REVIEW_MAP,
  GET_SUBMISSION_REVIEW_ACTIVITY,
} from "../../Queries";

interface Participant {
  id: string;
  name: string;
}

interface SubjectRow {
  /** group id (team mode) or user id (individual mode) */
  key: string;
  name: string;
  /** member user ids — excluded from this subject's reviewer options */
  memberIds: string[];
  memberNames: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  activity: SubmissionReviewActivity;
  onSaved?: () => void;
  /** FL-284: once reviews open the map is locked — view without editing. */
  readOnly?: boolean;
}

/**
 * Full-screen editor for the "Manually selected" reviewer mode: one row per
 * subject (team or student), each with a multi-select of the students who
 * will review it. Saved as explicit reviewer→subject pairings.
 */
export function ManualMappingDialog({
  open,
  onClose,
  activity,
  onSaved,
  readOnly = false,
}: Props) {
  const [t] = useTranslation();
  const isTeamMode = activity.submitterMode === "TM";
  const teams: ParticipantTeam[] = useMemo(
    () => activity.participantsPreview ?? [],
    [activity.participantsPreview],
  );

  const subjects: SubjectRow[] = useMemo(() => {
    if (isTeamMode) {
      return teams.map((team) => ({
        key: team.id,
        name: team.name,
        memberIds: team.members.map((m) => m.id),
        memberNames: team.members.map((m) => m.name),
      }));
    }
    // Individuals: the preview is one flat pseudo-team of all students
    return teams
      .flatMap((team) => team.members)
      .map((member) => ({
        key: member.id,
        name: member.name,
        memberIds: [member.id],
        memberNames: [],
      }));
  }, [isTeamMode, teams]);

  const reviewerPool: Participant[] = useMemo(() => {
    const seen = new Map<string, Participant>();
    for (const team of teams) {
      for (const member of team.members) {
        seen.set(member.id, member);
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [teams]);

  const reviewerById = useMemo(
    () => new Map(reviewerPool.map((p) => [p.id, p])),
    [reviewerPool],
  );

  // subject key -> reviewer ids, seeded from the saved pairings
  const [assignments, setAssignments] = useState<Record<string, string[]>>(
    () => {
      const initial: Record<string, string[]> = {};
      for (const pair of activity.manualPairs ?? []) {
        const key = pair.subjectGroupId ?? pair.subjectUserId;
        if (!key) continue;
        initial[key] = [...(initial[key] ?? []), pair.reviewerId];
      }
      return initial;
    },
  );

  const [setMap, { loading: saving }] = useMutation(SET_MANUAL_REVIEW_MAP, {
    refetchQueries: [
      {
        query: GET_SUBMISSION_REVIEW_ACTIVITY,
        variables: { pk: activity.id },
      },
    ],
    onCompleted: () => {
      onSaved?.();
      onClose();
    },
  });

  const handleSave = () => {
    const pairs = subjects.flatMap((subject) =>
      (assignments[subject.key] ?? []).map((reviewerId) =>
        isTeamMode
          ? { reviewerId, subjectGroupId: subject.key }
          : { reviewerId, subjectUserId: subject.key },
      ),
    );
    setMap({ variables: { activityId: activity.id, pairs } });
  };

  const uncoveredSubjects = subjects.filter(
    (subject) => (assignments[subject.key] ?? []).length === 0,
  );
  const assignedReviewerIds = new Set(Object.values(assignments).flat());
  const idleReviewers = reviewerPool.filter(
    (reviewer) => !assignedReviewerIds.has(reviewer.id),
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label={String(t("Close"))}
          >
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {t("Reviewer Mapping")}
          </Typography>
          {!readOnly && (
            <Button
              color="inherit"
              variant="outlined"
              onClick={handleSave}
              disabled={saving}
              startIcon={<Save />}
            >
              {t("Save Mapping")}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 1100, mx: "auto", width: "100%" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {readOnly
            ? t(
                "Reviews are open — the mapping is locked. This is the pairing reviews were created from.",
              )
            : t(
                isTeamMode
                  ? "For each team being reviewed, pick the students who will review it. Students from the team itself aren't offered."
                  : "For each student being reviewed, pick the students who will review their work.",
              )}
        </Typography>

        {(uncoveredSubjects.length > 0 || idleReviewers.length > 0) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {uncoveredSubjects.length > 0 && (
              <span>
                {uncoveredSubjects.length}{" "}
                {t(
                  isTeamMode
                    ? "team(s) have no reviewers yet."
                    : "student(s) have no reviewers yet.",
                )}{" "}
              </span>
            )}
            {idleReviewers.length > 0 && (
              <span>
                {idleReviewers.length}{" "}
                {t("student(s) are not assigned to review anyone:")}{" "}
                {idleReviewers.map((r) => r.name).join(", ")}
              </span>
            )}
          </Alert>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "35%" }}>
                {t(
                  isTeamMode ? "Team being reviewed" : "Student being reviewed",
                )}
              </TableCell>
              <TableCell>{t("Reviewed by")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.map((subject) => {
              const options = reviewerPool.filter(
                (reviewer) => !subject.memberIds.includes(reviewer.id),
              );
              const value = (assignments[subject.key] ?? [])
                .map((id) => reviewerById.get(id))
                .filter(Boolean) as Participant[];
              return (
                <TableRow key={subject.key}>
                  <TableCell sx={{ verticalAlign: "top", pt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {subject.name}
                    </Typography>
                    {subject.memberNames.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {subject.memberNames.join(", ")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      size="small"
                      disabled={readOnly}
                      options={options}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      value={value}
                      onChange={(_, selected) =>
                        setAssignments((prev) => ({
                          ...prev,
                          [subject.key]: selected.map((s) => s.id),
                        }))
                      }
                      renderTags={(selected, getTagProps) =>
                        selected.map((option, index) => {
                          const { key: _key, ...tagProps } = getTagProps({
                            index,
                          });
                          return (
                            <Chip
                              key={option.id}
                              label={option.name}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            value.length === 0
                              ? String(t("Add reviewers..."))
                              : undefined
                          }
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Dialog>
  );
}
